import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, Navigate, useSearchParams } from 'react-router';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useTheme, createThemedStyles } from '../contexts/ThemeContext';
import { useFeatureFlagContext } from '../contexts/FeatureFlagContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faExclamationTriangle, faSpinner } from '@/icons';
import { SignupProgress, TEAM_SIGNUP_STEPS } from '../components/SignupProgress';
import { SignupBackButton } from '../components/SignupBackButton';
import { useSignupSession } from '../contexts/SignupSessionContext';
import { authApiService, type SignupChargePaymentResponse } from '../services/authApiService';
import { BASIC_BASE, PRO_BASE, calcTotal, CARD_FEE_FIXED_LABEL, CARD_FEE_FORMULA_LABEL, CARD_FEE_RATE_LABEL, TAX_RATE_LABEL } from '../pricing/subscriptionPricing';
import { debugError } from '../utils/debug';
import { stripePromise, paymentReturnUrl, rememberPendingStripeReturn, clearPendingStripeReturn, readPendingStripeReturn, isAwaitingMicrodeposits, MICRODEPOSIT_SIGNUP_MESSAGE } from '../utils/stripe';
import { completeStripePayment, useStripePaymentCompletion, BANK_CONFIRMATION_MESSAGE } from '../hooks/useStripePaymentCompletion';

/*
 * 3D Secure on signup. A challenge shown in place (the usual case) finishes inside PaymentForm.
 * A redirect-based one leaves the page: Stripe returns to /billing/payment-return (the server
 * fixes that URL for its PaymentIntents), which sees the { kind: 'signup' } marker in
 * sessionStorage and forwards Stripe's query params here. This page holds the signup session
 * (localStorage), so it resumes: a setup_intent return charges the new method, a payment_intent
 * return calls finalize-payment.
 */

async function finalizeSignup(sessionToken: string): Promise<SignupChargePaymentResponse> {
    return (await authApiService.signupFinalizePayment({ sessionToken })).data;
}

function fmt(n: number) {
    return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const PaymentForm: React.FC<{
    sessionToken: string;
    billing: { total: number };
    founderName: string;
    founderEmail: string;
    onSuccess: () => void;
    onPaymentTypeChange: (isCard: boolean) => void;
    onSubmittingChange: (submitting: boolean) => void;
    /** The bank account needs micro-deposits, which signup cannot wait for. */
    onMicrodeposits: () => void;
}> = ({ sessionToken, billing, founderName, founderEmail, onSuccess, onPaymentTypeChange, onSubmittingChange, onMicrodeposits }) => {
    const stripe = useStripe();
    const elements = useElements();
    const theme = useTheme();
    const [submitting, setSubmitting] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);
    const [paymentComplete, setPaymentComplete] = useState(false);
    const [selectedPmType, setSelectedPmType] = useState<string>('card');
    const { completePayment, awaitingBank } = useStripePaymentCompletion();

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!stripe || !elements || submitting) return;
        setFormError(null);
        setSubmitting(true);
        onSubmittingChange(true);
        try {
            const { error: submitError } = await elements.submit();
            if (submitError) {
                setFormError(submitError.message || 'Please check your payment details.');
                setSubmitting(false);
                onSubmittingChange(false);
                return;
            }
            rememberPendingStripeReturn({ kind: 'signup', paymentMethodType: selectedPmType });
            let setupResult;
            try {
                setupResult = await stripe.confirmSetup({
                    elements,
                    confirmParams: { return_url: paymentReturnUrl() },
                    redirect: 'if_required',
                });
            } finally {
                clearPendingStripeReturn();
            }
            const { error: confirmError, setupIntent } = setupResult;
            if (confirmError) {
                setFormError(confirmError.message || 'Payment setup failed.');
                setSubmitting(false);
                onSubmittingChange(false);
                return;
            }
            if (!setupIntent) {
                setFormError('Payment setup could not be confirmed.');
                setSubmitting(false);
                onSubmittingChange(false);
                return;
            }
            if (isAwaitingMicrodeposits(setupIntent)) {
                // Not chargeable for 1-2 business days; never charge it. The page loads a new form.
                setSubmitting(false);
                onSubmittingChange(false);
                onMicrodeposits();
                return;
            }
            const pmId = typeof setupIntent.payment_method === 'string'
                ? setupIntent.payment_method
                : (setupIntent.payment_method as { id?: string })?.id ?? '';
            const pmType = selectedPmType;
            const { data: charged } = await authApiService.signupChargePayment({
                sessionToken,
                paymentMethodId: pmId,
                paymentMethodType: pmType,
            });
            const result = await completePayment(
                charged,
                () => finalizeSignup(sessionToken),
                { kind: 'signup', paymentMethodType: pmType },
            );
            if (!result.success) {
                setFormError(result.message || 'Payment failed. Please try again.');
                setSubmitting(false);
                onSubmittingChange(false);
                return;
            }
            onSuccess();
        } catch (err: unknown) {
            debugError('signupPayment', err);
            setFormError(err instanceof Error ? err.message : 'Payment failed. Please try again.');
            setSubmitting(false);
            onSubmittingChange(false);
        }
    };

    return (
        <form onSubmit={(e) => void handleSubmit(e)}>
            <div style={{ marginBottom: theme.spacing.md }}>
                <PaymentElement
                    options={{
                        defaultValues: {
                            billingDetails: {
                                name: founderName || undefined,
                                email: founderEmail || undefined,
                            },
                        },
                    }}
                    onChange={(e) => {
                        setPaymentComplete(e.complete);
                        setSelectedPmType(e.value.type);
                        onPaymentTypeChange(e.value.type !== 'us_bank_account');
                    }}
                />
            </div>
            {formError && (
                <div style={{
                    padding: theme.spacing.sm,
                    marginBottom: theme.spacing.sm,
                    backgroundColor: '#fee2e2',
                    color: '#991b1b',
                    borderRadius: theme.borderRadius.sm,
                    fontSize: theme.typography.fontSize.sm,
                }}>
                    {formError}
                </div>
            )}
            <button
                type="submit"
                disabled={!stripe || !elements || submitting || !paymentComplete}
                style={{
                    width: '100%',
                    padding: `${theme.spacing.md} ${theme.spacing.xl}`,
                    backgroundColor: !stripe || !elements || submitting || !paymentComplete ? theme.colors.border.default : theme.colors.primary,
                    color: '#fff',
                    border: 'none',
                    borderRadius: theme.borderRadius.md,
                    fontSize: theme.typography.fontSize.md,
                    fontWeight: theme.typography.fontWeight.semibold,
                    cursor: !stripe || !elements || submitting || !paymentComplete ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: theme.spacing.xs,
                }}
            >
                {submitting
                    ? <><FontAwesomeIcon icon={faSpinner} spin /> {awaitingBank ? BANK_CONFIRMATION_MESSAGE : 'Processing…'}</>
                    : `Pay ${fmt(billing.total)}`
                }
            </button>
        </form>
    );
};

export const RegisterPaymentPage: React.FC = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const themedStyles = createThemedStyles(theme);
    const { flags, isLoading: flagsLoading } = useFeatureFlagContext();
    const selfServeSignup = flags['SelfServiceSignup'] ?? false;
    const { session, updateSession } = useSignupSession();

    const [clientSecret, setClientSecret] = useState<string | null>(null);
    const [loadingIntent, setLoadingIntent] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [isCard, setIsCard] = useState(true);
    const [freeSubmitting, setFreeSubmitting] = useState(false);
    const [freeError, setFreeError] = useState<string | null>(null);
    const [paidSubmitting, setPaidSubmitting] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const [resuming, setResuming] = useState(false);
    const [resumeError, setResumeError] = useState<string | null>(null);
    const resumeStartedRef = useRef(false);
    const [microdepositNotice, setMicrodepositNotice] = useState(false);
    // Bumped to load a fresh SetupIntent: the old one is used up by a bank awaiting micro-deposits.
    const [intentNonce, setIntentNonce] = useState(0);

    useEffect(() => {
        document.title = 'Payment | Calculist';
    }, []);

    const plan = session.plan;
    const members = session.members;
    const founderRole = session.founderRole ?? 'attorney';
    const attorneyCount = members.filter(m => m.role === 'attorney').length + (founderRole === 'attorney' ? 1 : 0);
    const supportStaffCount = members.filter(m => m.role === 'supportStaff').length + (founderRole === 'supportStaff' ? 1 : 0);
    const basePerAttorney = plan === 'pro' ? PRO_BASE : BASIC_BASE;

    useEffect(() => {
        if (!session.sessionToken || plan === 'free' || !plan) return;
        let cancelled = false;
        setLoadingIntent(true);
        setLoadError(null);
        setClientSecret(null);
        authApiService.signupCreateSetupIntent({
            sessionToken: session.sessionToken,
        }).then(({ data }) => {
            if (cancelled) return;
            setClientSecret(data.clientSecret);
        }).catch((err: unknown) => {
            if (cancelled) return;
            debugError('signupCreateSetupIntent', err);
            setLoadError(err instanceof Error ? err.message : 'Could not initialize payment.');
        }).finally(() => { if (!cancelled) setLoadingIntent(false); });
        return () => { cancelled = true; };
    }, [session.sessionToken, plan, intentNonce]);

    const handleMicrodeposits = useCallback(() => {
        setMicrodepositNotice(true);
        setIntentNonce((n) => n + 1);
    }, []);

    const billing = calcTotal(basePerAttorney, attorneyCount, isCard);

    const handleFreeContinue = async () => {
        setFreeError(null);
        setFreeSubmitting(true);
        try {
            await authApiService.signupFinalizeFree({ sessionToken: session.sessionToken });
            updateSession({ paymentAmount: 0, paymentMethodType: null });
            navigate('/auth/register/team-complete');
        } catch (err: unknown) {
            debugError('signupFinalizeFree', err);
            setFreeError(err instanceof Error ? err.message : 'Could not finalize signup. Please try again.');
        } finally {
            setFreeSubmitting(false);
        }
    };

    const handlePaymentSuccess = useCallback(() => {
        updateSession({ paymentAmount: billing.total, paymentMethodType: isCard ? 'card' : 'us_bank_account' });
        navigate('/auth/register/team-complete');
    }, [navigate, updateSession, billing.total, isCard]);

    // Resume after a redirect-based bank authentication (see the comment at the top of the file).
    useEffect(() => {
        const setupSecret = searchParams.get('setup_intent_client_secret');
        const returnedFromPayment = !!searchParams.get('payment_intent');
        const sessionToken = session.sessionToken;
        if (!sessionToken || resumeStartedRef.current || (!setupSecret && !returnedFromPayment)) return;
        resumeStartedRef.current = true;

        const pending = readPendingStripeReturn();
        clearPendingStripeReturn();
        const pmType = pending?.kind === 'signup' && pending.paymentMethodType ? pending.paymentMethodType : 'card';
        setSearchParams({}, { replace: true });
        setResuming(true);
        setResumeError(null);

        void (async () => {
            try {
                let result: SignupChargePaymentResponse;
                if (returnedFromPayment) {
                    result = await finalizeSignup(sessionToken);
                } else {
                    const stripe = await stripePromise;
                    if (!stripe) throw new Error('Could not load the payment processor. Please try again.');
                    const { setupIntent, error } = await stripe.retrieveSetupIntent(setupSecret!);
                    if (!error && isAwaitingMicrodeposits(setupIntent)) {
                        throw new Error(MICRODEPOSIT_SIGNUP_MESSAGE);
                    }
                    if (error || !setupIntent || setupIntent.status !== 'succeeded') {
                        throw new Error(error?.message || 'Your bank did not confirm the payment method. Please try again.');
                    }
                    const pmId = typeof setupIntent.payment_method === 'string'
                        ? setupIntent.payment_method
                        : setupIntent.payment_method?.id ?? '';
                    result = (await authApiService.signupChargePayment({ sessionToken, paymentMethodId: pmId, paymentMethodType: pmType })).data;
                }
                result = await completeStripePayment(result, () => finalizeSignup(sessionToken), {
                    pending: { kind: 'signup', paymentMethodType: pmType },
                });
                if (!result.success) {
                    setResumeError(result.message || 'Payment failed. Please try again.');
                    return;
                }
                const card = pmType !== 'us_bank_account';
                updateSession({ paymentAmount: calcTotal(basePerAttorney, attorneyCount, card).total, paymentMethodType: card ? 'card' : 'us_bank_account' });
                navigate('/auth/register/team-complete');
            } catch (err: unknown) {
                debugError('signupPaymentResume', err);
                setResumeError(err instanceof Error ? err.message : 'Payment failed. Please try again.');
            } finally {
                setResuming(false);
            }
        })();
    }, [searchParams, setSearchParams, session.sessionToken, updateSession, navigate, basePerAttorney, attorneyCount]);

    if (flagsLoading) return null;
    if (!selfServeSignup) return <Navigate to="/auth/login" replace />;
    if (!session.sessionToken) return <Navigate to="/auth/register" replace />;
    if (!plan) return <Navigate to="/auth/register/plan" replace />;

    const wrapper = (content: React.ReactNode) => (
        <div style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            backgroundColor: theme.colors.background,
            padding: theme.spacing.md,
            paddingTop: theme.spacing.xl,
        }}>
            <div style={{ ...themedStyles.card.default, maxWidth: '600px', width: '100%' }}>
                <SignupProgress currentStep={3} steps={TEAM_SIGNUP_STEPS} />
                {content}
            </div>
        </div>
    );

    // Free plan
    if (plan === 'free') {
        return wrapper(
            <>
                <div style={{ textAlign: 'center', marginBottom: theme.spacing.xl }}>
                    <img src="/logo-name.svg" alt="Calculist" style={{ height: '48px', width: 'auto', marginBottom: theme.spacing.md }} />
                    <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '56px',
                        height: '56px',
                        borderRadius: '50%',
                        backgroundColor: '#f0fdf4',
                        color: '#16a34a',
                        fontSize: '24px',
                        marginBottom: theme.spacing.md,
                    }}>
                        <FontAwesomeIcon icon={faCheckCircle} />
                    </div>
                    <h1 style={{ ...themedStyles.text.heading, marginTop: 0, marginBottom: theme.spacing.xs }}>No payment required</h1>
                    <p style={{ ...themedStyles.text.muted, marginTop: 0 }}>You selected the Free plan. No credit card needed.</p>
                </div>
                {freeError && (
                    <div style={{ ...themedStyles.alert.error, marginBottom: theme.spacing.md }}>
                        <span>⚠️</span>
                        <div style={{ flex: 1 }}>{freeError}</div>
                    </div>
                )}
                <button
                    type="button"
                    onClick={() => void handleFreeContinue()}
                    disabled={freeSubmitting}
                    style={{
                        ...themedStyles.button.primary,
                        ...(freeSubmitting ? themedStyles.button.disabled : {}),
                        width: '100%',
                        padding: `${theme.spacing.md} ${theme.spacing.xl}`,
                    }}
                >
                    {freeSubmitting ? 'Please wait…' : 'Continue'}
                </button>
                <SignupBackButton onClick={() => navigate('/auth/register/team')} disabled={freeSubmitting} />
            </>
        );
    }

    // Paid plan
    return wrapper(
        <>
            <div style={{ textAlign: 'center', marginBottom: theme.spacing.xl }}>
                <img src="/logo-name.svg" alt="Calculist" style={{ height: '48px', width: 'auto', marginBottom: theme.spacing.md }} />
                <h1 style={{ ...themedStyles.text.heading, marginTop: 0, marginBottom: theme.spacing.xs }}>Payment</h1>
                <p style={{ ...themedStyles.text.muted, marginTop: 0 }}>
                    {plan === 'pro' ? 'Pro' : 'Basic'} plan — Annual subscription
                </p>
            </div>

            {/* Billing breakdown */}
            <div style={{
                backgroundColor: theme.colors.surfaceSecondary,
                border: `1px solid ${theme.colors.border.default}`,
                borderRadius: theme.borderRadius.lg,
                padding: theme.spacing.md,
                marginBottom: theme.spacing.md,
                display: 'flex',
                flexDirection: 'column',
                gap: theme.spacing.xs,
            }}>
                <div style={{ fontWeight: theme.typography.fontWeight.semibold, color: theme.colors.text.primary, marginBottom: theme.spacing.xs, fontSize: theme.typography.fontSize.sm }}>
                    Billing breakdown
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: theme.typography.fontSize.sm }}>
                    <span style={{ color: theme.colors.text.secondary }}>
                        {attorneyCount} attorney member{attorneyCount !== 1 ? 's' : ''} @ ${PRO_BASE.toLocaleString('en-US')}/yr
                    </span>
                    <span style={{ fontWeight: theme.typography.fontWeight.medium }}>{fmt(billing.base)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: theme.typography.fontSize.sm }}>
                    <span style={{ color: theme.colors.text.secondary }}>
                        {supportStaffCount} {supportStaffCount !== 1 ? 'support staff members' : 'support staff member'} @ free/yr
                    </span>
                    <span style={{ fontWeight: theme.typography.fontWeight.medium, color: '#16a34a' }}>FREE</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: theme.typography.fontSize.sm }}>
                    <span style={{ color: theme.colors.text.secondary }}>Iowa state tax ({TAX_RATE_LABEL})</span>
                    <span>{fmt(billing.tax)}</span>
                </div>
                {billing.cardFee > 0 ? (
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: theme.typography.fontSize.sm }}>
                        <span style={{ color: theme.colors.text.secondary }}>Card processing fee ({CARD_FEE_RATE_LABEL} + {CARD_FEE_FIXED_LABEL})</span>
                        <span>{fmt(billing.cardFee)}</span>
                    </div>
                ) : (
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: theme.typography.fontSize.sm }}>
                        <span style={{ color: theme.colors.text.secondary }}>Processing fee <span style={{ fontSize: theme.typography.fontSize.xs }}>— covered by Calculist</span></span>
                        <span>$0.00</span>
                    </div>
                )}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    paddingTop: theme.spacing.xs,
                    borderTop: `1px solid ${theme.colors.border.default}`,
                    fontWeight: theme.typography.fontWeight.bold,
                    fontSize: theme.typography.fontSize.md,
                    color: theme.colors.text.primary,
                    marginTop: theme.spacing.xs,
                }}>
                    <span>Total</span>
                    <span style={{ color: theme.colors.primary }}>{fmt(billing.total)}</span>
                </div>
            </div>

            <p style={{
                fontSize: theme.typography.fontSize.xs,
                color: theme.colors.text.muted,
                textAlign: 'center',
                marginTop: 0,
                marginBottom: theme.spacing.md,
            }}>
                All payments are final. Subscriptions are non-refundable.
            </p>

            {loadError && (
                <div style={{ ...themedStyles.alert.error, marginBottom: theme.spacing.md }}>
                    <span>⚠️</span>
                    <div style={{ flex: 1 }}>{loadError}</div>
                </div>
            )}

            {resumeError && (
                <div style={{ ...themedStyles.alert.error, marginBottom: theme.spacing.md }}>
                    <FontAwesomeIcon icon={faExclamationTriangle} />
                    <div style={{ flex: 1 }}>{resumeError}</div>
                </div>
            )}

            {resuming && (
                <div style={{ textAlign: 'center', padding: theme.spacing.lg, color: theme.colors.text.secondary }}>
                    <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: theme.spacing.xs }} />
                    {BANK_CONFIRMATION_MESSAGE}
                </div>
            )}

            {microdepositNotice && (
                <div role="status" style={{
                    marginBottom: theme.spacing.md,
                    padding: theme.spacing.md,
                    borderRadius: theme.borderRadius.md,
                    border: `1px solid ${theme.colors.status?.warning || '#d97706'}`,
                    backgroundColor: (theme.colors.status?.warning || '#d97706') + '18',
                    color: theme.colors.text.primary,
                    fontSize: theme.typography.fontSize.sm,
                }}>
                    {MICRODEPOSIT_SIGNUP_MESSAGE}
                </div>
            )}

            {!resuming && loadingIntent && (
                <div style={{ textAlign: 'center', padding: theme.spacing.lg, color: theme.colors.text.secondary }}>
                    <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: theme.spacing.xs }} />
                    Preparing payment form…
                </div>
            )}

            {!resuming && !loadingIntent && clientSecret && import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY && (
                <Elements
                    stripe={stripePromise}
                    options={{ clientSecret, appearance: { theme: 'stripe' } }}
                >
                    <PaymentForm
                        sessionToken={session.sessionToken}
                        billing={billing}
                        founderName={[session.firstName, session.lastName].filter(Boolean).join(' ')}
                        founderEmail={session.email ?? ''}
                        onSuccess={handlePaymentSuccess}
                        onPaymentTypeChange={setIsCard}
                        onSubmittingChange={setPaidSubmitting}
                        onMicrodeposits={handleMicrodeposits}
                    />
                    <SignupBackButton onClick={() => navigate('/auth/register/team')} disabled={paidSubmitting} />
                </Elements>
            )}

            {!loadingIntent && !clientSecret && !loadError && !import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY && (
                <div style={{ color: theme.colors.text.muted, fontSize: theme.typography.fontSize.sm, textAlign: 'center' }}>
                    Payment processing is not configured in this environment.
                </div>
            )}
        </>
    );
};
